// src/components/MessageWindow.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessagesSquare, Send, X, GripHorizontal } from "lucide-react";
import { api } from "../scripts/api";

/**
 * Draggable, non-blocking floating message window.
 * - No backdrop (user can keep using the app)
 * - Position persists via localStorage
 * - Sends to backend route: POST /api/message/send { text, topicId }
 */
export default function MessageWindow({ open, onClose, user }) {
  const display = useMemo(() => {
    const dn = String(user?.display_name || "").trim();
    const em = String(user?.email || "").trim();
    return dn || em || "";
  }, [user]);

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [log, setLog] = useState([]);

  const boxRef = useRef(null);
  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const STORAGE_KEY = "ims_msg_window_pos";

  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
    } catch {}
    // default: bottom-right-ish
    return { x: Math.max(20, window.innerWidth - 420), y: 90 };
  });

  // persist position
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {}
  }, [pos]);

  // keep window on-screen when resize
  useEffect(() => {
    const onResize = () => {
      const w = 380;
      const h = 360;
      setPos((p) => ({
        x: Math.min(Math.max(10, p.x), Math.max(10, window.innerWidth - w - 10)),
        y: Math.min(Math.max(10, p.y), Math.max(10, window.innerHeight - h - 10)),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onHeaderMouseDown = (e) => {
    // only left click
    if (e.button !== 0) return;

    dragRef.current.dragging = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.originX = pos.x;
    dragRef.current.originY = pos.y;

    // prevent text selection while dragging
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      const w = 380;
      const h = 360;

      const nextX = dragRef.current.originX + dx;
      const nextY = dragRef.current.originY + dy;

      setPos({
        x: Math.min(Math.max(10, nextX), Math.max(10, window.innerWidth - w - 10)),
        y: Math.min(Math.max(10, nextY), Math.max(10, window.innerHeight - h - 10)),
      });
    };

    const onUp = () => {
      dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  const sendMessage = async () => {
    const t = String(text || "").trim();
    if (!t) return;

    setBusy(true);
    setStatus("");

    // keep a local log regardless of backend response
    const stamp = new Date().toISOString();
    setLog((prev) => [{ dir: "out", text: t, at: stamp }, ...prev]);

    try {
      // ✅ correct route (singular): /api/message/send
      // backend should accept { text, topicId }
      const { data } = await api.post("/api/message/send", {
        topicId: 4, // pick your topic; you’re already using 4 for login
        text: `[IMS] ${display ? `${display}: ` : ""}${t}`,
      });

      if (data?.ok) {
        setStatus("Sent to Telegram ✅");
        setText("");
      } else {
        setStatus(data?.error || "Telegram returned non-ok ⚠️");
      }
    } catch (e) {
      const d = e?.response?.data;
      setStatus(d?.error || d?.message || e?.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <div
      ref={boxRef}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 380,
        height: 360,
        zIndex: 2000,
        borderRadius: 14,
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* draggable header */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          cursor: "grab",
          userSelect: "none",
          padding: "10px 12px",
          borderBottom: "1px solid var(--card-border)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(0,0,0,0.02)",
        }}
        title="Drag to move"
      >
        <GripHorizontal size={16} className="muted" />
        <MessagesSquare size={18} />
        <div style={{ fontWeight: 900 }}>Messages</div>
        <div style={{ flex: 1 }} />
        <button className="btn-outline" type="button" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>

      {/* body */}
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>
        <div className="muted" style={{ fontSize: 12 }}>
          Sends to Telegram topic <b>#4</b>. Ctrl/⌘ + Enter to send.
        </div>

        {status ? (
          <div className="muted" style={{ fontSize: 12 }}>
            {status}
          </div>
        ) : null}

        {/* log */}
        <div
          className="card"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {log.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>
              No messages yet.
            </div>
          ) : (
            log.map((m, idx) => (
              <div key={idx} style={{ fontSize: 12, lineHeight: 1.3 }}>
                <div className="muted" style={{ opacity: 0.85 }}>
                  {m.dir === "out" ? "You" : "Telegram"} • {new Date(m.at).toLocaleString()}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
              </div>
            ))
          )}
        </div>

        {/* composer */}
        <textarea
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type a message…"
          rows={3}
          style={{ resize: "none" }}
        />

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn"
            type="button"
            onClick={sendMessage}
            disabled={busy || !String(text || "").trim()}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Send size={16} />
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
